import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { PageSchema } from '@/components/SEOSchema';
import { productCategories } from '@/mocks/products';
import { SITE_URL } from '@/lib/seo';

function ScrollToTop() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  return null;
}

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const allProducts = useMemo(
    () => productCategories.flatMap((cat) => cat.products),
    [],
  );

  const filteredProducts = useMemo(() => {
    let products = activeCategory === 'all'
      ? allProducts
      : productCategories.find((c) => c.id === activeCategory)?.products || [];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      products = products.filter(
        (p) => p.title.toLowerCase().includes(q) || p.category.toLowerCase().includes(q),
      );
    }
    return products;
  }, [activeCategory, searchQuery, allProducts]);

  const categories = useMemo(
    () => [{ id: 'all', name: t('products.allCategories', 'All Categories') }, ...productCategories.map((c) => ({ id: c.id, name: c.name }))],
    [t],
  );

  return (
    <div className="min-h-screen bg-background-50">
      <PageSchema
        type="CollectionPage"
        name={t('products.title', 'Product Catalog')}
        description={t('products.subtitle', 'Explore our curated collection of products. Click any item to view details and purchase.')}
        url={`${SITE_URL}${i18n.language === 'pt' ? '/produtos/' : i18n.language === 'de' ? '/produkte/' : '/products/'}`}
        lang={i18n.language}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-white/20 blur-3xl" />
            <div className="absolute bottom-10 right-10 w-60 h-60 rounded-full bg-accent-400/30 blur-3xl" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white font-heading mb-4">
              {t('products.title', 'Product Catalog')}
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8">
              {t('products.subtitle', 'Explore our curated collection of products. Click any item to view details and purchase.')}
            </p>
            <div className="flex items-center justify-center gap-3 max-w-lg mx-auto">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('products.searchPlaceholder', 'Search products...')}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/95 backdrop-blur-sm border-0 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
                />
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-4 py-3 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors text-sm whitespace-nowrap"
                >
                  {t('products.clear', 'Clear')}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-20 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-white font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  {cat.id !== 'all' && (
                    <span className={`ml-1.5 text-xs ${activeCategory === cat.id ? 'text-white/70' : 'text-foreground-400'}`}>
                      ({productCategories.find((c) => c.id === cat.id)?.products.length || 0})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Product Grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              {t('products.showing', 'Showing')} <strong className="text-foreground-900">{filteredProducts.length}</strong> {t('products.items', 'items')}
            </p>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-shopping-bag-3-line text-5xl text-foreground-300 mb-4 block" />
              <p className="text-foreground-500 text-lg">{t('products.noResults', 'No products found')}</p>
              <button
                onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
                className="mt-4 px-6 py-2 rounded-full bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors"
              >
                {t('products.viewAll', 'View All Products')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {filteredProducts.map((product) => (
                <a
                  key={product.id}
                  href={product.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group bg-white rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="aspect-square overflow-hidden bg-background-100 relative">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=minimal%20product%20placeholder%20icon%20on%20soft%20gray%20background&width=400&height=400&seq=product-fallback&orientation=squarish';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                    <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-medium text-primary-600">
                        <i className="ri-external-link-line" />
                        Buy
                      </span>
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] md:text-xs font-medium mb-2">
                      {product.category}
                    </span>
                    <h3 className="text-xs md:text-sm font-medium text-foreground-900 leading-snug line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                      {product.title}
                    </h3>
                    <p className="text-sm md:text-base font-bold text-primary-600">
                      {product.price}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}